import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { formatDistanceToNow, addDays, addYears } from 'date-fns'
import { ShortenedUrl, deleteUrl, renewUrl } from '../lib/api'
import styles from './UrlRow.module.css'

interface Props {
  url: ShortenedUrl
  isExpired?: boolean
  onDeleted: (code: string) => void
  onRenewed?: (updated: ShortenedUrl) => void
}

const RENEW_PRESETS = [
  { label: '7 days',  getDate: () => addDays(new Date(), 7) },
  { label: '30 days', getDate: () => addDays(new Date(), 30) },
  { label: '1 year',  getDate: () => addYears(new Date(), 1) },
  { label: 'Never',   getDate: (): Date | null => null },
] as const

export default function UrlRow({ url, isExpired = false, onDeleted, onRenewed }: Props) {
  const [copied, setCopied] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [renewOpen, setRenewOpen] = useState(false)
  const [renewing, setRenewing] = useState(false)

  const shortUrl = `${import.meta.env.VITE_API_BASE_URL ?? window.location.origin}/${url.shortCode}`

  function copy() {
    navigator.clipboard.writeText(shortUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleDelete() {
    if (!confirm(`Delete /${url.shortCode}?`)) return
    setDeleting(true)
    try {
      await deleteUrl(url.shortCode)
      onDeleted(url.shortCode)
    } catch {
      setDeleting(false)
    }
  }

  async function handleRenew(getDate: () => Date | null) {
    setRenewing(true)
    try {
      const date = getDate()
      const updated = await renewUrl(url.shortCode, date?.toISOString())
      setRenewOpen(false)
      onRenewed?.(updated)
    } catch {
      // keep panel open so user can retry
    } finally {
      setRenewing(false)
    }
  }

  return (
    <motion.div
      className={`${styles.row} ${isExpired ? styles.expired : ''}`}
      layout
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 8, height: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* ── Main row ── */}
      <div className={styles.codes}>
        <div className={styles.shortRow}>
          <span className={styles.short}>{shortUrl}</span>
          {isExpired && <span className={styles.expiredBadge}>Expired</span>}
        </div>
        <span className={styles.original} title={url.originalUrl}>{url.originalUrl}</span>
      </div>

      <div className={styles.meta}>
        <span className={styles.clicks}>{url.clickCount.toLocaleString()} clicks</span>
        <span className={styles.age}>{formatDistanceToNow(new Date(url.createdAt))} ago</span>
      </div>

      <div className={styles.actions}>
        {!isExpired && (
          <button className={styles.iconBtn} onClick={copy} title="Copy">
            {copied ? '✓' : '⎘'}
          </button>
        )}
        <Link className={styles.iconBtn} to={`/stats/${url.shortCode}`} title="Stats">
          ↗
        </Link>
        {isExpired && onRenewed && (
          <button
            className={`${styles.iconBtn} ${styles.renewBtn} ${renewOpen ? styles.renewBtnActive : ''}`}
            onClick={() => setRenewOpen(v => !v)}
            title="Renew link"
            disabled={renewing}
          >
            ↺
          </button>
        )}
        <button
          className={`${styles.iconBtn} ${styles.danger}`}
          onClick={handleDelete}
          disabled={deleting}
          title="Delete"
        >
          ✕
        </button>
      </div>

      {/* ── Inline renew panel (spans full row width, no overflow issues) ── */}
      <AnimatePresence initial={false}>
        {renewOpen && (
          <motion.div
            className={styles.renewPanel}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
          >
            <div className={styles.renewPanelInner}>
              <span className={styles.renewLabel}>Renew for…</span>
              <div className={styles.renewPresets}>
                {RENEW_PRESETS.map(preset => (
                  <button
                    key={preset.label}
                    className={styles.renewPreset}
                    onClick={() => handleRenew(preset.getDate)}
                    disabled={renewing}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
